import type { FC } from 'react';
import React, { useState } from 'react';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { Icon } from '../icon';
import { Input } from '../input';
import { Button } from '../button';
import { message } from 'src/common/components/message';
import { isUnsafeUrl } from '@flowus/common/url';
import { Tooltip } from '@flowus/common/tooltip';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export enum LinkWidgetType {
  external = 0,
  internal = 1,
}

export interface LinkWidgetProp {
  linkType?: LinkWidgetType;
  onLinkChange?: (value: string) => void;
  onSetLink?: (linkUrl?: string) => void;
  onClearLink?: (isFocus?: boolean) => void;
  onCloseModal: () => void;
  inLink?: boolean;
  link: string;
  isInternalLink?: boolean;
}

interface UniversalLinkProps {
  isInternalLink: boolean;
}

const ExternalLinkPanel: FC<Omit<LinkWidgetProp, 'linkType'> & UniversalLinkProps> = ({
  inLink,
  link,
  onLinkChange,
  onSetLink,
  onCloseModal,
  isInternalLink,
  onClearLink,
}) => {
  const { t } = useAppTranslation();
  const [editable, setEditable] = useState(isInternalLink);
  return (
    <>
      {inLink && !isInternalLink && !editable ? (
        <>
          <div className="flex items-center w-full justify-between space-x-1">
            <div className="flex items-center space-x-1 min-w-[140px]">
              <Icon name={'IcGlobeShare'} size="middle" />
              <span className="text-ellipsis">{link}</span>
            </div>
            <div className="shrink-0 flex items-center space-x-1">
              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.edit_link')}
                onClick={() => {
                  setEditable(true);
                }}
              >
                <Icon name={'IcBtnEdit'} size="small" />
              </Tooltip>

              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.copy_link')}
                onClick={() => {
                  void writeTextInClipboard(link, {
                    message: t('common.components.link_widget.link_copied'),
                  });
                }}
              >
                <Icon name={'IcDuplicate'} size="small" />
              </Tooltip>
              <Tooltip
                className="p-1 animate-hover"
                popup={t('common.components.link_widget.clear_link')}
                onClick={() => {
                  onClearLink?.();
                  onCloseModal();
                }}
              >
                <Icon name={'IcTrash'} size="small" />
              </Tooltip>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-between">
          <Input
            autoFocus
            placeholder={t('common.components.link_widget.enter_webpage_link')}
            value={isInternalLink ? '' : link}
            className="w-full mx-2.5 my-2.5 h-8"
            onEnter={() => {
              if (isUnsafeUrl(link)) {
                message.error(t('components.image.external_link_invalid'));
                return;
              }
              onSetLink?.(isInternalLink ? '' : link);
              onCloseModal();
            }}
            onChange={(value) => {
              onLinkChange?.(value);
            }}
          />
          <Button
            colorType="active"
            size="large"
            onClick={() => {
              if (isUnsafeUrl(link)) {
                message.error(t('components.image.external_link_invalid'));
                return;
              }
              onSetLink?.(isInternalLink ? '' : link);
              onCloseModal();
            }}
          >
            {t('common.components.link_widget.confirm')}
          </Button>
        </div>
      )}
    </>
  );
};

export const LinkWidget: FC<LinkWidgetProp> = ({
  // linkType = LinkWidgetType.external,
  isInternalLink = false,
  ...props
}) => {
  return (
    <div className="next-modal w-[360px] p-1 flex items-center text-t2 text-grey3">
      <ExternalLinkPanel {...props} isInternalLink={isInternalLink} />
    </div>
  );
};
